/**
 * The four outcomes of a post, rendered from `PostingStatusOut` and nothing else.
 *
 * One renderer, used by **both** screens that can be looking at a post: S7, where a
 * manager arrived by confirming, and the **verifying** screen, where the app arrived
 * because a reply never came. The states are identical once BC answers — what
 * differs is the sentence above them — and rendering them twice would be two chances
 * to describe the same outcome differently.
 *
 * **The four are genuinely different, and the differences are the point:**
 *
 * 1. **Posted** — the document number, prominently. It is what ties the count out
 *    against the item ledger in BC, and it is the one string anyone will be asked
 *    for later.
 * 2. **Posted, and a post-completion step reported an error** — `posted` and
 *    `failed` are separate fields for exactly this. Codeunit 23 commits the post
 *    before some of its own late steps, so error text on a `Posted` session means
 *    the adjustment **is in the ledger** and something afterwards complained.
 *    Rendering that as a failure would send someone to re-post work that already
 *    posted, which is the single worst thing this screen could do.
 * 3. **Error** — nothing posted, and Business Central's own words **verbatim**. They
 *    name the line and the item; a paraphrase loses both.
 * 4. **`presumed_stuck`** — past BC's own 30-minute threshold, where every measured
 *    post is under 8 seconds. **D-5, ruled 2026-08-19**: the app reports and does
 *    nothing. It says the count may or may not have posted, that nobody should post
 *    it again, and to contact **Copeland** — recovering a stranded session decides
 *    whether a half-finished post counts as posted, and the retry it enables can
 *    double-post.
 *
 * Plus two non-outcomes it must also render honestly, because a poll can see them:
 * a healthy post still running, and — only reachable from verifying — a session
 * Business Central never took. That last card is the one the **2026-08-24 ruling**
 * moves: after a minute of `InReview` the app stops saying "contact Copeland" and
 * offers the post, so the card takes `reoffer` and says which of the three things is
 * true rather than one sentence that would be wrong in two of them.
 *
 * **Nothing here sends anything.** No mutation, no button, no `method: "POST"`. The
 * asymmetry that makes the whole verifying design possible is that reading is safe
 * and repeatable while the post is not, and this component is on the reading side of
 * it.
 */
import type { PostingStatus } from "../api/types";

/** The statuses that mean Business Central has not started posting this count. */
const NOT_STARTED = ["Open", "InReview"];

export function PostingOutcome({
  status,
  sessionId,
  reoffer = null,
}: {
  status: PostingStatus;
  sessionId: string;
  /**
   * Where the never-started rule stands, for the one card it changes.
   *
   * `null` on S7, which does not apply the rule at all: it is reached by
   * confirming, not by losing a reply, and has no `post_attempted_at` to measure
   * from. `"watching"` while the verifying screen is still inside the minute, and
   * `"offered"` once it has put the confirm back — at which point this card must
   * stop telling a manager not to post the count the screen below is offering.
   */
  reoffer?: "watching" | "offered" | null;
}) {
  const postedWithComplaint = status.posted && Boolean(status.error_text);
  const notStarted = !status.terminal && NOT_STARTED.includes(status.status);

  return (
    <div data-testid="poll-result">
      {status.posted && (
        <div
          className="card outcome outcome--posted"
          data-testid={postedWithComplaint ? "posted-with-error" : "posted-outcome"}
        >
          {/* Said first and said plainly, in both variants. A manager reading this
              card must never come away unsure whether the count is in the ledger. */}
          <h3 data-testid="verified-posted">This count posted to Business Central.</h3>
          {status.posting_document_no ? (
            <>
              <p className="small">Posted as document</p>
              <p className="outcome__document" data-testid="document-no">
                <code>{status.posting_document_no}</code>
              </p>
              <p className="muted small">
                That number is how this count ties out against the item ledger in
                Business Central. Nothing further is needed here.
              </p>
            </>
          ) : (
            <p className="muted small">
              Business Central reported no document number for it, which is unusual
              enough to be worth mentioning to Copeland — the count itself posted.
            </p>
          )}
          {postedWithComplaint && (
            <>
              <p>
                <strong>A step after the posting reported this:</strong>
              </p>
              <p className="error-text" data-testid="error-text">
                {status.error_text}
              </p>
              <p className="muted small">
                {/* The distinction this whole card exists to preserve. */}
                <strong>This is not a failed post.</strong> The adjustment is in the
                ledger under the document number above, and a step that ran afterwards
                complained. <strong>Do not post this count again</strong> — that would
                move the inventory twice. Show this message to Copeland.
              </p>
            </>
          )}
        </div>
      )}

      {status.failed && (
        <div className="card error outcome outcome--error" data-testid="error-outcome">
          <h3>This count did not post. Nothing moved in Business Central.</h3>
          <p>Business Central's own words:</p>
          {/* Verbatim. It names the line and the item that stopped the post. */}
          <p className="error-text verbatim" data-testid="error-text">
            {status.error_text ?? "Business Central recorded no error text."}
          </p>
        </div>
      )}

      {status.presumed_stuck && (
        <div className="card error outcome outcome--stuck" data-testid="presumed-stuck">
          <h3>This count has been posting far longer than any post ever takes.</h3>
          <p>
            <strong>It may or may not have posted.</strong> Business Central has not
            said, and this app will not guess: the difference is a question about the
            ledger, and only looking at the ledger answers it.
          </p>
          <p>
            <strong>Do not post it again.</strong> Posting a count twice moves
            inventory twice, and unpicking that is manual work for somebody.
          </p>
          <p>
            <strong>Contact Copeland</strong> with this session id. Recovering a
            stranded count is a decision a person makes after looking at Business
            Central, not something this screen should offer a button for.
          </p>
          <p className="muted small">
            Session <code data-testid="stuck-session-id">{sessionId}</code>
            {typeof status.elapsed_seconds === "number" && (
              <> · posting for {Math.round(status.elapsed_seconds / 60)} minutes</>
            )}
          </p>
        </div>
      )}

      {!status.terminal && !status.presumed_stuck && !notStarted && (
        <div className="card notice outcome outcome--working" data-testid="posting-in-flight">
          <h3>Posting in Business Central…</h3>
          <p className="small">
            {/* The server's sentence, not one assembled here. */}
            {status.summary}
          </p>
          <p className="muted small">
            This screen re-checks by itself
            {typeof status.next_poll_after_seconds === "number"
              ? ` every ${Math.round(status.next_poll_after_seconds)}s`
              : ""}
            , and nothing is re-sent when it does.
          </p>
        </div>
      )}

      {notStarted && (
        <div className="card notice outcome outcome--not-started" data-testid="not-started">
          <h3>Business Central still shows this count as {status.status}.</h3>
          <p>
            The posting codeunit marks a count <code>Posting</code> as its first act,
            so a count still reading <code>{status.status}</code>{" "}
            <strong>has not started posting</strong>.
          </p>
          {reoffer === null && (
            <p>
              <strong>Do not post it again from here anyway.</strong> A request that was
              already on its way can still arrive, and this screen cannot see the
              difference between "never sent" and "sent a moment ago". It keeps asking;
              if it stays like this,{" "}
              <strong>contact Copeland before anyone posts this count</strong>.
            </p>
          )}
          {reoffer === "watching" && (
            /* The minute is running. Saying so beats both alternatives: "do not post"
               would be retracted a moment later, and silence would read as a screen
               that has given up. */
            <p data-testid="not-started-watching">
              <strong>Nothing to do yet.</strong> A request already on its way can still
              arrive, so this screen waits and keeps asking. If Business Central is
              still showing this count as {status.status} a minute after the attempt,
              the first attempt never reached it — and this screen will say so and offer
              to post the count.
            </p>
          )}
          {reoffer === "offered" && (
            <p data-testid="not-started-settled">
              Business Central has now said this on{" "}
              <strong>two checks more than a minute apart from the attempt</strong>,
              which is why the post is offered below.
            </p>
          )}
          <p className="muted small">
            Session <code>{sessionId}</code>
          </p>
        </div>
      )}
    </div>
  );
}
